"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { EVENTS } from "@/lib/analytics/trackEvent";
import { type TrackedPill, CentroidTracker } from "@/lib/pill-counter/centroid-tracker";
import { PerformanceManager } from "@/lib/pill-counter/performance-manager";
import { useTrackEvent } from "@/hooks/useTrackEvent";

type WorkerMessage =
  | { type: "OPENCV_READY" }
  | { type: "NOT_READY" }
  | { type: "CLEANED_UP" }
  | { type: "OPENCV_LOAD_FAILED"; payload?: { reason?: string; message?: string } }
  | { type: "DETECTIONS"; payload?: { detections?: Array<{ x: number; y: number; radius: number }>; frameId?: number } };

export type CameraStatus = "idle" | "requesting" | "active" | "denied" | "error" | "no_support" | "opencv_failed";

export interface LiveState {
  status: CameraStatus;
  trackedPills: TrackedPill[];
  pillCount: number;
  stableCount: number;
  fps: number;
  preset: string;
  errorMessage: string | null;
  isOpenCvReady: boolean;
  isOpenCvLoading: boolean;
}

export interface LiveControls {
  start: () => Promise<void>;
  stop: () => void;
  capture: () => TrackedPill[];
  videoRef: React.RefObject<HTMLVideoElement | null>;
  overlayCanvasRef: React.RefObject<HTMLCanvasElement | null>;
  processingCanvasRef: React.RefObject<HTMLCanvasElement | null>;
}

export function useLiveDetection(): [LiveState, LiveControls] {
  const [status, setStatus] = useState<CameraStatus>("idle");
  const [trackedPills, setTrackedPills] = useState<TrackedPill[]>([]);
  const [fps, setFps] = useState(0);
  const [preset, setPreset] = useState("HIGH");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isOpenCvReady, setIsOpenCvReady] = useState(false);
  const [isOpenCvLoading, setIsOpenCvLoading] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const processingCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const workerRef = useRef<Worker | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const trackerRef = useRef(new CentroidTracker());
  const perfRef = useRef(new PerformanceManager());
  const rafRef = useRef<number | null>(null);
  const processingRef = useRef(false);
  const frameIdRef = useRef(0);
  const lastFrameTimeRef = useRef(0);
  const frameCountRef = useRef(0);
  const isActiveRef = useRef(false);
  const processLoopRef = useRef<() => void>(() => {});
  const { track } = useTrackEvent();

  const stop = useCallback(() => {
    isActiveRef.current = false;
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    trackerRef.current.reset();
    processingRef.current = false;
    setTrackedPills([]);
    setStatus((prev) => (prev === "opencv_failed" ? prev : "idle"));
    void track(EVENTS.LIVE_SCAN_STOPPED);
  }, [track]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    setIsOpenCvLoading(true);

    let worker: Worker;
    try {
      worker = new Worker("/workers/pill-detection.worker.js");
    } catch {
      setStatus("opencv_failed");
      setIsOpenCvLoading(false);
      setErrorMessage("Could not start detection engine. Please use Photo mode instead.");
      return;
    }

    worker.onmessage = (e: MessageEvent<WorkerMessage>) => {
      const data = e.data;
      switch (data.type) {
        case "OPENCV_READY":
          setIsOpenCvReady(true);
          setIsOpenCvLoading(false);
          return;
        case "OPENCV_LOAD_FAILED":
          setIsOpenCvReady(false);
          setIsOpenCvLoading(false);
          setStatus("opencv_failed");
          setErrorMessage(
            data.payload?.reason === "timeout"
              ? "Detection engine took too long to load. Check your connection and try again, or use Photo mode instead."
              : "Detection engine failed to load. Please use Photo mode instead.",
          );
          void track(EVENTS.OPENCV_LOAD_FAILED, {
            reason: data.payload?.reason ?? "error",
            message: data.payload?.message ?? null,
          });
          return;
        case "NOT_READY":
          processingRef.current = false;
          return;
        case "DETECTIONS": {
          if (!isActiveRef.current) return;
          const now = Date.now();
          const frameTime = now - lastFrameTimeRef.current;
          lastFrameTimeRef.current = now;
          perfRef.current.recordFrameTime(frameTime);
          frameCountRef.current += 1;

          if (frameCountRef.current % 30 === 0) {
            perfRef.current.autoAdjust();
            setPreset(perfRef.current.getPresetName());
            setFps(perfRef.current.getAverageFps());
          }

          const incoming = Array.isArray(data.payload?.detections) ? data.payload?.detections : [];
          const mapped = incoming.map((d) => ({
            x: 20 + d.x * 0.6,
            y: 20 + d.y * 0.6,
            radius: d.radius * 0.6,
          }));
          const tracked = trackerRef.current.update(mapped);
          setTrackedPills([...tracked]);
          processingRef.current = false;
          return;
        }
        default:
          return;
      }
    };

    worker.onerror = () => {
      processingRef.current = false;
    };
    workerRef.current = worker;

    return () => {
      worker.postMessage({ type: "CLEANUP" });
      worker.terminate();
      workerRef.current = null;
    };
  }, [track]);

  useEffect(() => {
    processLoopRef.current = () => {
      if (!isActiveRef.current) return;

      const video = videoRef.current;
      const canvas = processingCanvasRef.current;
      const worker = workerRef.current;
      if (!video || !canvas || !worker || !isOpenCvReady) {
        rafRef.current = requestAnimationFrame(processLoopRef.current);
        return;
      }
      if (video.readyState < 2 || video.paused) {
        rafRef.current = requestAnimationFrame(processLoopRef.current);
        return;
      }

      if (!processingRef.current && perfRef.current.shouldProcessFrame()) {
        const config = perfRef.current.getConfig();
        const sourceWidth = video.videoWidth || 0;
        const sourceHeight = video.videoHeight || 0;
        if (sourceWidth <= 0 || sourceHeight <= 0) {
          rafRef.current = requestAnimationFrame(processLoopRef.current);
          return;
        }

        const targetW = config.targetWidth;
        const scale = targetW / sourceWidth;
        const targetH = Math.round(sourceHeight * scale);
        if (canvas.width !== targetW) canvas.width = targetW;
        if (canvas.height !== targetH) canvas.height = targetH;

        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) {
          rafRef.current = requestAnimationFrame(processLoopRef.current);
          return;
        }

        ctx.drawImage(video, 0, 0, targetW, targetH);
        const cx = Math.round(targetW * 0.2);
        const cy = Math.round(targetH * 0.2);
        const cw = Math.round(targetW * 0.6);
        const ch = Math.round(targetH * 0.6);
        const imageData = ctx.getImageData(cx, cy, cw, ch);

        processingRef.current = true;
        frameIdRef.current += 1;
        worker.postMessage(
          {
            type: "PROCESS_FRAME",
            payload: {
              imageData,
              width: cw,
              height: ch,
              config,
              frameId: frameIdRef.current,
            },
          },
          [imageData.data.buffer],
        );
      }

      rafRef.current = requestAnimationFrame(processLoopRef.current);
    };
  }, [isOpenCvReady]);

  const start = useCallback(async () => {
    if (status === "opencv_failed") return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus("no_support");
      setErrorMessage("Camera not supported on this browser. Please use Chrome on Android or Safari on iPhone.");
      return;
    }

    setStatus("requesting");
    const constraints: MediaStreamConstraints[] = [
      {
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      },
      { video: { width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false },
      { video: true, audio: false },
    ];

    let stream: MediaStream | null = null;
    let lastError: DOMException | null = null;

    for (const c of constraints) {
      try {
        stream = await navigator.mediaDevices.getUserMedia(c);
        break;
      } catch (err) {
        const domErr = err as DOMException;
        lastError = domErr;
        if (domErr.name === "NotAllowedError" || domErr.name === "PermissionDeniedError") break;
      }
    }

    if (!stream) {
      const denied = lastError?.name === "NotAllowedError" || lastError?.name === "PermissionDeniedError";
      if (denied) {
        setStatus("denied");
        setErrorMessage("Camera permission denied. Please allow camera access in your browser settings and try again.");
        void track(EVENTS.CAMERA_PERMISSION_DENIED);
      } else {
        setStatus("error");
        setErrorMessage("Could not access camera. Please check your device and try again.");
      }
      return;
    }

    streamRef.current = stream;
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      try {
        await videoRef.current.play();
      } catch {}
    }

    trackerRef.current.reset();
    frameCountRef.current = 0;
    processingRef.current = false;
    isActiveRef.current = true;
    setTrackedPills([]);
    setStatus("active");
    setErrorMessage(null);
    void track(EVENTS.LIVE_SCAN_STARTED);
    lastFrameTimeRef.current = Date.now();
    rafRef.current = requestAnimationFrame(processLoopRef.current);
  }, [status, track]);

  useEffect(() => {
    return () => {
      isActiveRef.current = false;
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const capture = useCallback((): TrackedPill[] => {
    return [...trackedPills];
  }, [trackedPills]);

  const stableCount = trackedPills.filter((p) => p.isStable).length;

  return [
    {
      status,
      trackedPills,
      pillCount: trackedPills.length,
      stableCount,
      fps,
      preset,
      errorMessage,
      isOpenCvReady,
      isOpenCvLoading,
    },
    {
      start,
      stop,
      capture,
      videoRef,
      overlayCanvasRef,
      processingCanvasRef,
    },
  ];
}
